import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { SORT_OPTIONS, STAGES, STAGE_LABELS, formatHeight, type Stage } from '@accheyrishtey/shared';
import { api } from '../lib/api';
import type { ProfileListItem, ProfilePage } from '../lib/types';
import { initials } from '../lib/format';
import {
  GENDER_VIEW_OPTIONS,
  applyDashboardSortValue,
  dashboardSortValue,
  type DashboardSortValue,
} from '../lib/dashboardFilters';
import { readViewMode, saveViewMode, type ViewMode } from '../lib/viewPreference';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { LoadingState } from '../components/ui/LoadingState';
import { Select } from '../components/ui/Field';
import { FilterPanel } from '../components/dashboard/FilterPanel';
import { FilterChips } from '../components/dashboard/FilterChips';
import { FILTER_PARAM_KEYS } from '../components/dashboard/filterConfig';

interface LinkState { active: { url: string } | null }

function ProfilePortrait({ profile }: { profile: ProfileListItem }) {
  return profile.photo_url
    ? <img src={profile.photo_url} alt="" loading="lazy" />
    : <div className="portrait-placeholder"><span>{initials(profile.full_name)}</span></div>;
}

function ProfileCard({ profile, onStar }: { profile: ProfileListItem; onStar: () => void }) {
  return (
    <motion.article className="profile-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Link to={`/profiles/${profile.id}`} className="profile-card__link" aria-label={`Open ${profile.full_name}`}>
        <div className="profile-card__portrait"><ProfilePortrait profile={profile} /></div>
        <div className="profile-card__content">
          <h2>{profile.full_name}</h2>
          <p className="profile-card__facts">{profile.age} years <i/> {formatHeight(profile.height_cm) || 'Height —'}</p>
          <p>{profile.gender} · {profile.marital_status}</p>
          {profile.maslak ? <p className="muted">{profile.maslak}</p> : null}
          <div className="profile-card__foot">
            <span className={`stage stage--${profile.stage}`}>{STAGE_LABELS[profile.stage]}</span>
            {profile.note_count ? <span className="note-count"><Icon name="note" />{profile.note_count} {profile.note_count === 1 ? 'note' : 'notes'}</span> : <span />}
          </div>
        </div>
      </Link>
      <button className={profile.is_starred ? 'star is-active' : 'star'} onClick={onStar}
        aria-label={profile.is_starred ? 'Remove star' : 'Star profile'}>
        <Icon name="star" fill={profile.is_starred ? 'currentColor' : 'none'} />
      </button>
    </motion.article>
  );
}

function ProfileTable({ profiles, onStar }: { profiles: ProfileListItem[]; onStar: (profile: ProfileListItem) => void }) {
  return <div className="table-wrap"><table className="profile-table"><thead><tr>
    <th>Name</th><th>Age</th><th>Height</th><th>Gender</th><th>Maslak</th><th>Stage</th><th>Notes</th><th>Star</th>
  </tr></thead><tbody>{profiles.map((profile) => <tr key={profile.id}>
    <td><Link to={`/profiles/${profile.id}`}><span className="table-avatar"><ProfilePortrait profile={profile}/></span>{profile.full_name}</Link></td>
    <td>{profile.age}</td><td>{formatHeight(profile.height_cm) || '—'}</td><td>{profile.gender}</td>
    <td>{profile.maslak || '—'}</td><td>{STAGE_LABELS[profile.stage]}</td><td>{profile.note_count}</td>
    <td><button className={profile.is_starred ? 'star is-active' : 'star'} onClick={() => onStar(profile)}><Icon name="star" fill={profile.is_starred ? 'currentColor' : 'none'}/></button></td>
  </tr>)}</tbody></table></div>;
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [view, setView] = useState<ViewMode>(() => readViewMode(localStorage));
  const stage = (searchParams.get('stage') ?? 'active') as Stage;
  const page = Number(searchParams.get('page') ?? 1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (search.trim()) next.set('q', search.trim()); else next.delete('q');
      next.delete('page');
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, searchParams, setSearchParams]);

  const queryString = searchParams.toString();
  const profiles = useQuery({
    queryKey: ['profiles', queryString],
    queryFn: () => api.get<ProfilePage>(`/profiles?${queryString}`),
  });
  const stats = useQuery({
    queryKey: ['profile-stats'],
    queryFn: () => api.get<Record<Stage, number>>('/profiles/stats'),
  });
  const links = useQuery({ queryKey: ['form-links'], queryFn: () => api.get<LinkState>('/form-links') });
  const star = useMutation({
    mutationFn: (id: string) => api.patch(`/profiles/${id}/star`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profiles'] }),
  });

  const setParam = (key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key); else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };
  const activeFilterCount = Array.from(FILTER_PARAM_KEYS).filter((key) => searchParams.has(key)).length;
  const changeView = (next: ViewMode) => { setView(next); saveViewMode(localStorage, next); };
  const copyFormLink = async () => {
    if (links.data?.active?.url) await navigator.clipboard.writeText(links.data.active.url);
  };
  const items = profiles.data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((profiles.data?.total ?? 0) / 24));
  const title = stage === 'active' ? 'Profiles' : STAGE_LABELS[stage];

  return <div className="dashboard page-enter">
    <header className="dashboard-head">
      <div><h1>{title}</h1><p>Muslim matrimony profile management</p></div>
      <label className="search-box"><Icon name="search"/><span className="sr-only">Search</span>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search every profile and note" />
      </label>
      <Button variant="secondary" icon={<Icon name="link"/>} onClick={copyFormLink} disabled={!links.data?.active}>Copy form link</Button>
    </header>

    <nav className="stage-tabs" aria-label="Profile stages">{STAGES.map((item) => <button key={item}
      className={stage === item ? 'is-active' : ''} onClick={() => setParam('stage', item === 'active' ? undefined : item)}>
      {STAGE_LABELS[item]} <span>{stats.data?.[item] ?? 0}</span>
    </button>)}</nav>

    <div className="dashboard-toolbar">
      <Button variant="ghost" icon={<Icon name="filter"/>} onClick={() => setFilterOpen(true)}>Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</Button>
      <Select aria-label="Sort profiles" value={dashboardSortValue(searchParams)} onChange={(event) => setSearchParams(applyDashboardSortValue(searchParams, event.target.value as DashboardSortValue))}>
        <optgroup label="Order by">{SORT_OPTIONS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</optgroup>
        <optgroup label="Show only">{GENDER_VIEW_OPTIONS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</optgroup>
      </Select>
      <div className="view-toggle"><button className={view === 'grid' ? 'is-active' : ''} onClick={() => changeView('grid')} aria-label="Grid view"><Icon name="grid"/></button><button className={view === 'list' ? 'is-active' : ''} onClick={() => changeView('list')} aria-label="List view"><Icon name="list"/></button></div>
    </div>
    <FilterChips params={searchParams} onChange={setSearchParams}/>

    {profiles.isLoading ? <LoadingState /> : profiles.isError ? <div className="empty-state"><h2>Could not load profiles</h2><Button onClick={() => profiles.refetch()}>Try again</Button></div>
      : items.length === 0 ? <div className="empty-state"><div className="empty-state__arch">AR</div><h2>{activeFilterCount || search ? 'No matching profiles' : 'No profiles yet'}</h2><p>{activeFilterCount || search ? 'Try clearing the search or filters.' : 'Share your public form link to receive the first profile.'}</p>{activeFilterCount || search ? <Button onClick={() => { setSearch(''); setSearchParams({}); }}>Clear filters</Button> : <Button onClick={copyFormLink}>Copy form link</Button>}</div>
      : view === 'grid' ? <div className="profile-grid">{items.map((profile) => <ProfileCard key={profile.id} profile={profile} onStar={() => star.mutate(profile.id)}/>)}</div>
      : <ProfileTable profiles={items} onStar={(profile) => star.mutate(profile.id)}/>}

    {items.length ? <footer className="pagination"><span>Showing {((page - 1) * 24) + 1}–{Math.min(page * 24, profiles.data?.total ?? 0)} of {profiles.data?.total ?? 0}</span><div><Button variant="ghost" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>Previous</Button><span>Page {page} of {totalPages}</span><Button variant="ghost" disabled={page >= totalPages} onClick={() => setParam('page', String(page + 1))}>Next</Button></div></footer> : null}

    <FilterPanel open={filterOpen} params={searchParams} total={profiles.data?.total ?? 0} onChange={setSearchParams} onClose={() => setFilterOpen(false)}/>
  </div>;
}
